import type { AiCostLimitCreatedAction } from '@Application/AiCostLimits/Limits/Actions/aiCostLimitCreated';
import type { AiCostLimitDeletedAction } from '@Application/AiCostLimits/Limits/Actions/aiCostLimitDeleted';
import type { AiCostLimitUpdatedAction } from '@Application/AiCostLimits/Limits/Actions/aiCostLimitUpdated';
import type { AiCostLimitsFetchedAction } from '@Application/AiCostLimits/Limits/Actions/aiCostLimitsFetched';
import type { AiCostLimitSubjectsFetchedAction } from '@Application/AiCostLimits/Subjects/Actions/aiCostLimitSubjectsFetched';
import type { AiCostLimitUsersFetchedAction } from '@Application/AiCostLimits/Users/Actions/aiCostLimitUsersFetched';
import type { FacilityCreatedAction } from '@Application/Facilities/Facilities/Actions/facilityCreated';
import type { FacilityDeletedAction } from '@Application/Facilities/Facilities/Actions/facilityDeleted';
import type { FacilityUpdatedAction } from '@Application/Facilities/Facilities/Actions/facilityUpdated';
import type { OrganizationCreatedAction } from '@Application/Organizations/Organizations/Actions/organizationCreated';
import type { OrganizationDeletedAction } from '@Application/Organizations/Organizations/Actions/organizationDeleted';
import type { OrganizationUpdatedAction } from '@Application/Organizations/Organizations/Actions/organizationUpdated';
import type { UserRevokedAction } from '@Application/Security/Authentications/Actions/userRevoked';
import AiCostLimitSubjectsView from '@UI/Store/AiCostLimits/Views/AiCostLimitSubjectsView';
import AiCostLimitUserView from '@UI/Store/AiCostLimits/Views/AiCostLimitUserView';
import AiCostLimitUsersListView from '@UI/Store/AiCostLimits/Views/AiCostLimitUsersListView';
import AiCostLimitView from '@UI/Store/AiCostLimits/Views/AiCostLimitView';
import AiCostLimitsListView from '@UI/Store/AiCostLimits/Views/AiCostLimitsListView';
import { createSliceReducer, registerStore } from '@verclam/synapse';

export type AiCostLimitsStore = {
	limits: AiCostLimitsListView | null;
	users: AiCostLimitUsersListView | null;
	subjects: AiCostLimitSubjectsView | null;
};

const initialState: AiCostLimitsStore = {
	limits: null,
	users: null,
	subjects: null,
};

type Actions =
	| AiCostLimitsFetchedAction
	| AiCostLimitCreatedAction
	| AiCostLimitUpdatedAction
	| AiCostLimitDeletedAction
	| AiCostLimitUsersFetchedAction
	| AiCostLimitSubjectsFetchedAction
	| OrganizationCreatedAction
	| OrganizationUpdatedAction
	| OrganizationDeletedAction
	| FacilityCreatedAction
	| FacilityUpdatedAction
	| FacilityDeletedAction
	| UserRevokedAction;

const whenAiCostLimitsFetched = (
	store: AiCostLimitsStore,
	action: AiCostLimitsFetchedAction,
): AiCostLimitsStore => ({
	...store,
	limits: new AiCostLimitsListView(
		action.accountId,
		action.payload.map((limit) => new AiCostLimitView(limit)),
	),
});

// A mutation answered for another account than the one loaded is ignored: the list stays consistent.
const whenAiCostLimitCreated = (
	store: AiCostLimitsStore,
	action: AiCostLimitCreatedAction,
): AiCostLimitsStore => {
	if (store.limits === null || store.limits.accountId !== action.accountId) {
		return store;
	}

	return { ...store, limits: store.limits.add(new AiCostLimitView(action.payload)) };
};

const whenAiCostLimitUpdated = (
	store: AiCostLimitsStore,
	action: AiCostLimitUpdatedAction,
): AiCostLimitsStore => {
	if (store.limits === null || store.limits.accountId !== action.accountId) {
		return store;
	}

	return { ...store, limits: store.limits.replace(new AiCostLimitView(action.payload)) };
};

const whenAiCostLimitDeleted = (
	store: AiCostLimitsStore,
	action: AiCostLimitDeletedAction,
): AiCostLimitsStore => {
	if (store.limits === null || store.limits.accountId !== action.accountId) {
		return store;
	}

	return { ...store, limits: store.limits.remove(action.payload.limitId) };
};

const whenAiCostLimitUsersFetched = (
	store: AiCostLimitsStore,
	action: AiCostLimitUsersFetchedAction,
): AiCostLimitsStore => ({
	...store,
	users: new AiCostLimitUsersListView(
		action.accountId,
		action.payload.map((user) => new AiCostLimitUserView(user)),
	),
});

const whenAiCostLimitSubjectsFetched = (
	store: AiCostLimitsStore,
	action: AiCostLimitSubjectsFetchedAction,
): AiCostLimitsStore => ({
	...store,
	subjects: new AiCostLimitSubjectsView(action.accountId, action.payload),
});

// The structures and facilities a limit can bound change with these mutations: the list is dropped so
// the readers fetch it again.
const whenSubjectsMayHaveChanged = (store: AiCostLimitsStore): AiCostLimitsStore => ({
	...store,
	subjects: null,
});

const whenUserRevoked = (): AiCostLimitsStore => initialState;

export default registerStore<'aiCostLimitsStore', Actions>(
	'aiCostLimitsStore',
	createSliceReducer({
		AI_COST_LIMITS_FETCHED: whenAiCostLimitsFetched,
		AI_COST_LIMIT_CREATED: whenAiCostLimitCreated,
		AI_COST_LIMIT_UPDATED: whenAiCostLimitUpdated,
		AI_COST_LIMIT_DELETED: whenAiCostLimitDeleted,
		AI_COST_LIMIT_USERS_FETCHED: whenAiCostLimitUsersFetched,
		AI_COST_LIMIT_SUBJECTS_FETCHED: whenAiCostLimitSubjectsFetched,
		ORGANIZATION_CREATED: whenSubjectsMayHaveChanged,
		ORGANIZATION_UPDATED: whenSubjectsMayHaveChanged,
		ORGANIZATION_DELETED: whenSubjectsMayHaveChanged,
		FACILITY_CREATED: whenSubjectsMayHaveChanged,
		FACILITY_UPDATED: whenSubjectsMayHaveChanged,
		FACILITY_DELETED: whenSubjectsMayHaveChanged,
		USER_REVOKED: whenUserRevoked,
	}),
	initialState,
);
